// react
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
// node-modules
import axios, { AxiosResponse } from "axios";
// mui
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  DialogContentText,
  useTheme,
  PopoverOrigin,
  IconButton,
  Popover,
  Typography,
} from "@mui/material";
// src
import { config } from "../config/config";
import { LoadingButton } from "@mui/lab";
import { EDisplayType, ProjectDetailContext } from "../pages/ProjectDetail";
import { deleteContext } from "../pages/Home";
import { coordinateSystem, verticalShift } from "../config/SelectListData";
import DeleteARFileDialog from "./DeleteARFileDialog";
import { Data, EDataType } from "./ProjectDetail/Contexts/CoordinateContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import FormatUtil from "./Utility/ProjectComUtil";
import { Common } from "../../common/src";
import { projectDetailAPIs } from "../api/project";

enum OutputType {
  PointCloud = "point_cloud",
  IFC = "ifc",
}

type Props = {
  open: boolean;
  close: () => void;
  projectID: string;
  projectName: string;
  las_file?: string;
  setErrorMsg: Dispatch<SetStateAction<string>>;
  setErrorSnackbar: Dispatch<SetStateAction<boolean>>;
  ifc_file?: string;
  vertical?: string;
  horizontal?: string;
  jobsiteName?: string;
  project?: Common.Project;
  // type?: EDisplayType
};

type CustomPopoverProps = {
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  data: Data[];
  customColor: { color: string };
  styleColon: React.CSSProperties;
};

const CustomPopover: React.FC<CustomPopoverProps> = ({
  anchorOrigin = { vertical: "bottom", horizontal: "center" },
  transformOrigin = { vertical: "top", horizontal: "center" },
  data,
  customColor,
  styleColon,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const stylePopKey: React.CSSProperties = {
    width: "120px",
    color: customColor.color,
    fontSize: "14px",
    overflowWrap: "break-word",
    wordWrap: "break-word",
  };

  const stylePopValue: React.CSSProperties = {
    color: customColor.color,
    fontSize: "14px",
    overflowWrap: "break-word",
    wordWrap: "break-word",
  };

  return (
    <span style={{ marginLeft: "10px" }}>
      <IconButton onClick={handleClick} style={{ padding: 0 }}>
        <FontAwesomeIcon icon={faCircleExclamation} style={customColor} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
      >
        <Typography sx={{ p: 2 }}>
          {data.map((item, index) => (
            <div style={{ display: "flex" }} key={index}>
              <div style={stylePopKey}>{item.left}</div>
              <div style={styleColon}>:</div>
              <div style={stylePopValue}>
                {Number(FormatUtil.removeDecimalPoint(Number(item.right), 5))}
              </div>
            </div>
          ))}
        </Typography>
      </Popover>
    </span>
  );
};

const ARListDialog = (props: Props) => {
  const { t } = useTranslation();
  const [lasWhileLoading, setLasWhileLoading] = useState(false);
  const [ifcWhileLoading, setIfcWhileLoading] = useState(false);
  const [outputType, setOutputType] = useState<OutputType>();
  const [openDeleteFileDialog, setOpenDeleteFileDialog] = useState(false);
  const { getProjectList } = useContext(deleteContext);
  const { displayType } = useContext(ProjectDetailContext);

  useEffect(() => {
    if (props.open) {
      document.body.style.cssText = `position: fixed; top: -${window.scrollY}px; overflow-y: scroll; width: 100%;`;
    }
    return () => {
      document.body.style.cssText = ``;
    };
  }, [props.open]);

  const handleClose = () => {
    props.close();
  };

  const navigate = useNavigate();
  const location = useLocation();
  const searchCondition =
    location.state && (location.state as any).searchCondition
      ? (location.state as any).searchCondition
      : undefined;

  const handleClickRemoveFile = (type: OutputType) => {
    setOutputType(type);
    setOpenDeleteFileDialog(true);
  };

  const handleCloseDeleteFileDialog = () => {
    setOpenDeleteFileDialog(false);
    setOutputType(undefined);
  };

  const removeFile = async () => {
    switch (outputType) {
      case OutputType.PointCloud:
        setLasWhileLoading(true);
        break;
      case OutputType.IFC:
        setIfcWhileLoading(true);
    }
    try {
      await axios
        .delete(
          `${config.serverHost}/api/v1/project/${props.projectID}/ar/${outputType}`,
          {
            withCredentials: true,
          }
        )
        .then((res: AxiosResponse<any>) => {
          location.pathname === "/"
            ? getProjectList(new AbortController())
            : searchCondition
            ? navigate(`/`, { state: { searchCondition: searchCondition } })
            : navigate("/");
          if (displayType === EDisplayType.iOS) {
            (window as any).webkit.messageHandlers.callback.postMessage({
              type: "back",
              props: {},
            });
          }
        });
    } catch (err) {
      console.error(err);
      props.setErrorSnackbar(true);
      props.setErrorMsg(t("AR_DELETE_FAILED"));
    } finally {
      setLasWhileLoading(false);
      setIfcWhileLoading(false);
      handleCloseDeleteFileDialog();
      props.close();
    }
  };

  const theme = useTheme();

  const customColor = {
    color: theme.palette.base.contrast,
  };

  const customModalColor = {
    color: theme.palette.base.modal,
  };

  const customDialogBackgroundColor = {
    color: theme.palette.base.dialogBackground,
  };

  const customIfcLoadingButtonColor = {
    background: props.ifc_file ? theme.palette.btn.error.backgroundColor : "",
    border: theme.palette.btn.error.border,
    boxShadow: "none",
  };

  const customLasLoadingButtonColor = {
    background: props.las_file ? theme.palette.btn.error.backgroundColor : "",
    border: theme.palette.btn.error.border,
    boxShadow: "none",
  };

  function getLabelByValue(
    value: string,
    array: Array<any>
  ): string | undefined {
    const found = array.find((item) => item.value === value);
    return found ? found.label : "";
  }

  const [localizedStorageData, setLocalizedStorageData] = useState<Data[]>([
    { id: "rotation", left: t("ROTATION"), right: "" },
    { id: "originEasting", left: t("ORIGIN_EASTING"), right: "" },
    { id: "originNorthing", left: t("ORIGIN_NORTHING"), right: "" },
    { id: "originLatitude", left: t("ORIGIN_LATITUDE"), right: "" },
    { id: "originLongitude", left: t("ORIGIN_LONGITUDE"), right: "" },
    { id: "scaleFactor", left: t("SCALE_FACTOR"), right: "" },
    { id: "verticalOffset", left: t("VERTICAL_OFFSET"), right: "" },
    { id: "inclineX", left: t("INCLINE_X"), right: "" },
    { id: "inclineY", left: t("INCLINE_Y"), right: "" },
  ]);
  const [currentType, setCurrentType] = useState<EDataType>(EDataType.NONE);

  const fetchCS = async (projectId: string) => {
    try {
      const res: AxiosResponse<Common.ProjectViDoc> =
        await projectDetailAPIs.coordinate.get(projectId);

      console.log("data:", res.data);
      if (res.data.horizontal) {
        setCurrentType(EDataType.EPSG);
      } else if (res.data.originEasting) {
        setCurrentType(EDataType.LOCALIZE);
      } else {
        setCurrentType(EDataType.NONE);
        res.data.conditionFlag = false;
      }
      if (res.data.conditionFlag) {
        const newVidocData = localizedStorageData.map((item) => {
          item.right = res.data[item.id as keyof Common.ProjectViDoc];
          return item;
        });
        setLocalizedStorageData(newVidocData);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (props.open && props.projectID) {
      fetchCS(props.projectID);
    }
  }, [props.open, props.projectID]);

  const styleColon: React.CSSProperties = {
    minHeight: "32px",
    fontSize: "16px",
    marginRight: "10px",
  };

  const styleKey: React.CSSProperties = {
    minHeight: "32px",
    minWidth: "150px",
    width: "150px",
    color: customColor.color,
    fontSize: "16px",
    marginLeft: "10px",
    overflowWrap: "break-word",
    wordWrap: "break-word",
  };

  const styleValue: React.CSSProperties = {
    minHeight: "32px",
    width: "100%",
    maxWidth: "260px",
    color: customColor.color,
    fontSize: "16px",
    marginRight: "10px",
    overflowWrap: "break-word",
    wordWrap: "break-word",
  };

  return (
    <Dialog
      open={props.open}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      PaperProps={{
        className: "coordinate-dialog",
        style: {
          background: customModalColor.color,
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: customDialogBackgroundColor.color,
          },
        },
      }}
    >
      <DialogTitle
        style={customColor}
        className="title-name"
        id="alert-dialog-title"
      >
        {t("MORE_INFOR")}
        <Button
          style={customColor}
          className="x-btn btn-remove"
          onClick={handleClose}
        >
          ×
        </Button>
        <div className="border-comm" />
      </DialogTitle>
      <DialogContent>
        <div>
          {currentType === EDataType.LOCALIZE &&
          localizedStorageData &&
          localizedStorageData.length > 0 ? (
            <>
              <div style={{ textAlign: "start" }}>
                【{t("COORDINATE_SYSTEM")}】
              </div>
              <div
                style={{
                  display: "grid",
                  margin: "15px 0",
                  marginLeft: "15px",
                  textAlign: "start",
                  gridTemplateColumns: "2fr 1fr 8fr",
                }}
              >
                <DialogContentText style={customColor}>
                  {t("COORDINATE_SYSTEM")}
                </DialogContentText>
                <DialogContentText style={customColor}>：</DialogContentText>
                <DialogContentText style={customColor}>
                  {t("COORDINATE_SYSTEM_JOBSITE")}({props.jobsiteName})
                  <CustomPopover
                    data={localizedStorageData}
                    customColor={customColor}
                    styleColon={styleColon}
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                  />
                </DialogContentText>
              </div>
            </>
          ) : (
            <div>
              <div style={{ textAlign: "start" }}>
                【{t("COORDINATE_SYSTEM")}】
              </div>
              <div
                style={{
                  display: "grid",
                  margin: "15px 0",
                  marginLeft: "15px",
                  textAlign: "start",
                  gridTemplateColumns: "2fr 1fr 8fr",
                }}
              >
                <DialogContentText style={customColor}>
                  {t("COORDINATE_SYSTEM")}
                </DialogContentText>
                <DialogContentText style={customColor}>：</DialogContentText>
                <DialogContentText style={customColor}>
                  {props.horizontal
                    ? getLabelByValue(props.horizontal, coordinateSystem)
                    : ""}
                </DialogContentText>
              </div>
              <div
                style={{
                  display: "grid",
                  margin: "15px 0",
                  marginLeft: "15px",
                  textAlign: "start",
                  gridTemplateColumns: "2fr 1fr 8fr",
                }}
              >
                <DialogContentText style={customColor}>
                  {t("VERTICAL_COORDINATE_SYSTEM")}
                </DialogContentText>
                <DialogContentText style={customColor}>：</DialogContentText>
                <DialogContentText style={customColor}>
                  {props.vertical
                    ? getLabelByValue(props.vertical, verticalShift)
                    : ""}
                </DialogContentText>
              </div>
            </div>
          )}

          <div style={{ textAlign: "start" }}>【{t("AR_FILE_LIST")}】</div>
          <div
            style={{
              display: "grid",
              margin: "15px 0",
              marginLeft: "15px",
              textAlign: "start",
              gridTemplateColumns: "2fr 1fr 6.5fr 1.5fr",
            }}
          >
            <DialogContentText style={customColor}>
              {t("POINT_CLOUD_DATA")}
            </DialogContentText>
            <DialogContentText style={customColor}>：</DialogContentText>
            <DialogContentText
              style={{
                ...customColor,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {props.las_file}
            </DialogContentText>
            <div>
              <LoadingButton
                style={customLasLoadingButtonColor}
                loading={lasWhileLoading}
                size="small"
                variant="contained"
                disabled={
                  !props.las_file ||
                  lasWhileLoading ||
                  props.project.status?.ar_processing ===
                    Common.PROJECT_STATUS_ENUM.PROCESSING
                }
                onClick={(e) => handleClickRemoveFile(OutputType.PointCloud)}
              >
                {t("REMOVE")}
              </LoadingButton>
            </div>
          </div>
          <div
            style={{
              display: "grid",
              margin: "15px 0",
              marginLeft: "15px",
              textAlign: "start",
              gridTemplateColumns: "2fr 1fr 6.5fr 1.5fr",
            }}
          >
            <DialogContentText style={customColor}>
              {t("DESIGN_DATA")}
            </DialogContentText>
            <DialogContentText style={customColor}>：</DialogContentText>
            <DialogContentText
              style={{
                ...customColor,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {props.ifc_file}
            </DialogContentText>
            <div>
              <LoadingButton
                style={customIfcLoadingButtonColor}
                loading={ifcWhileLoading}
                size="small"
                variant="contained"
                disabled={
                  !props.ifc_file ||
                  lasWhileLoading ||
                  props.project.status?.ar_processing ===
                    Common.PROJECT_STATUS_ENUM.PROCESSING
                }
                onClick={(e) => handleClickRemoveFile(OutputType.IFC)}
              >
                {t("REMOVE")}
              </LoadingButton>
            </div>
          </div>
        </div>
      </DialogContent>
      {openDeleteFileDialog && (
        <DeleteARFileDialog
          open={openDeleteFileDialog}
          isLoading={lasWhileLoading || ifcWhileLoading}
          confirm={removeFile}
          close={handleCloseDeleteFileDialog}
        />
      )}
    </Dialog>
  );
};
export default ARListDialog;
